app.controller("settingTabsController", [
	"$scope",
	"AppointmentAPI",
	"PracticeService",
	"TasksAPI",
	"Session",
	"countryConfig",
	"$mdDialog",
	"$timeout",
	"initialSection",
	"initialFocus",
	"FEATURES",
	function ($scope, AppointmentAPI, PracticeService, TasksAPI, Session, countryConfig, $mdDialog, $timeout, initialSection, initialFocus, FEATURES) {
		$scope.API = AppointmentAPI;
		$scope.PracticeAPI = PracticeService;
		$scope.TasksAPI = TasksAPI;
		$scope.$mdDialog = $mdDialog;

		const user = Session.user || {};
		$scope.fullName = [user.title, user.firstName, user.lastName].filter(Boolean).join(" ");
		$scope.userRole = (user.roles || []).join(", ");
		$scope.initials = ((user.firstName || "").charAt(0) + (user.lastName || "").charAt(0)).toUpperCase();

		const practices = Session.practiceOptions || [];
		const sessionPracticeId = Session.practice && Session.practice.id;

		// Practice-scoped settings are listed per practice rather than hidden behind a
		// selector, so the practice you are about to change is always on screen. Each
		// practice only offers the sections its own features allow.
		function practiceItems(practice) {
			// The practice you are working in reads its features from the session's own copy, which is
			// kept current when the practice changes mid-session - as the nav's are.
			const live = practice.id === sessionPracticeId && Session.practice && Session.practice.practiceFeatures;
			const features = live || practice.practiceFeatures || {};
			const tasks = !!(features.sendTasksWithinPracticeFeature || features.sendTasksOutsidePracticeFeature);
			// Not just the reminder channels: the screen behind this tab reads appointment
			// types and per-type reminder settings, both of which need the calendar.
			const reminders = Session.hasAppointmentReminders(practice);
			// Claim items are the Tyro item list, which only exists where the practice claims
			// and bills - Australia, with an accounting connection behind it.
			const claimItems = countryConfig.medicareFeatureEnabled && !!features.billingFeature;
			// Rooms are practice-wide and need the room permission at this practice, so nobody else is
			// shown a screen whose every change the API would refuse.
			const rooms = !!features.calendarFeature && Session.hasPermissionAt(practice, "SCHEDULE_ROOM_MANAGE");
			// Premium IOL prices are the practice's, quoted on the pre-op plan at private practices. Shown to
			// those who can read them; the screen itself is read only for anyone who cannot change them.
			const premiumLensPrices = FEATURES.PREMIUMLENS && practice.type === "PRIVATE"
				&& Session.hasPermissionAt(practice, "SURGERY_PREMIUM_LENS_PRICE_VIEW");
			// Staff and role permissions are each shown only where you hold the permission their screen loads with.
			const staff = Session.hasPermissionAt(practice, "PRACTICE_USER_VIEW");
			const rolePermissions = Session.hasPermissionAt(practice, "PRACTICE_ROLE_MANAGE");

			// Practice-wide settings first, then the ones that are yours alone but still
			// set up per practice. The scope badge on each screen spells out which.
			// Reminders sit with appointment types, which they are written per: anyone at the
			// practice can set them up for any clinician there, so they are not "yours" alone.
			return [
				{ key: "practice", label: "Practice details", icon: "fa-building", visible: true },
				{ key: "staff", label: "Staff", icon: "fa-users", visible: staff },
				{ key: "rolePermissions", label: "Role permissions", icon: "fa-shield", visible: rolePermissions },
				{ key: "claimItems", label: "Claim items", icon: "fa-list-ul", visible: claimItems },
				{ key: "appointmentTypes", label: "Appointment types", icon: "fa-calendar", visible: !!features.calendarFeature },
				{ key: "rooms", label: "Rooms", icon: "fa-door-open", visible: rooms },
				// Shown to everyone with the calendar, so staff can see when the practice is closed; only
				// Practice Admins can change it.
				{ key: "practiceLeave", label: "Practice leave", icon: "fa-calendar-times-o", visible: !!features.calendarFeature },
				{ key: "premiumLensPrices", label: "Premium lens prices", icon: "fa-tag", visible: premiumLensPrices },
				// What glasses and contact lens jobs offer and charge, beside the other lens prices.
				{ key: "lensDispensing", label: "Lens extras", icon: "fa-eye", visible: !!features.lensDispensingFeature },
				{ key: "reminders", label: "Appointment reminders", icon: "fa-bell", visible: reminders },
				{ key: "taskLabels", label: "Task labels", icon: "fa-tags", visible: tasks },
				{ key: "taskAutomation", label: "Your task automation", icon: "fa-magic", visible: tasks },
			].filter((item) => item.visible);
		}

		// Reminders and task automation are configured per practice, so they live under
		// the practice they apply to. What is left here is genuinely practice-independent.
		const accountGroup = {
			title: "Your account",
			practiceId: null,
			items: [{ key: "profile", label: "Profile", icon: "fa-user" }],
		};

		// The practice you are working in today leads the rail. It is the one you almost
		// always came here to change, and on an account with a lot of practices it could
		// otherwise sit below the fold - the one group already open, out of sight. The
		// rest keep the order the session gave them.
		const orderedPractices = practices
			.filter((practice) => practice.id === sessionPracticeId)
			.concat(practices.filter((practice) => practice.id !== sessionPracticeId));

		$scope.groups = [accountGroup].concat(
			orderedPractices.map((practice) => ({
				title: practice.name,
				practiceId: practice.id,
				isPractice: true,
				isCurrent: practice.id === sessionPracticeId,
				// Practices you are not working in today start folded away, so the rail
				// stays short for anyone who belongs to a lot of them.
				collapsed: practice.id !== sessionPracticeId,
				items: practiceItems(practice),
			}))
		);

		$scope.practiceCount = practices.length;

		// One practice open at a time - opening a second folds the first away.
		function openOnly(practiceId) {
			$scope.groups.forEach(function (group) {
				if (group.isPractice) {
					group.collapsed = group.practiceId !== practiceId;
				}
			});
		}

		$scope.toggleGroup = function (group) {
			if (!group.isPractice) {
				return;
			}
			openOnly(group.collapsed ? group.practiceId : null);
		};

		// A section is only meaningful together with the practice it belongs to.
		function findSection(key) {
			const holders = $scope.groups.filter((group) => group.items.some((item) => item.key === key));
			if (!holders.length) {
				return null;
			}
			// A section that exists for several practices opens on the one you are
			// working in today.
			const preferred = holders.find((group) => group.practiceId === sessionPracticeId) || holders[0];
			return { key: key, practiceId: preferred.practiceId };
		}

		function firstSection() {
			for (let i = 0; i < $scope.groups.length; i++) {
				if ($scope.groups[i].items.length) {
					return { key: $scope.groups[i].items[0].key, practiceId: $scope.groups[i].practiceId };
				}
			}
			return { key: null, practiceId: null };
		}

		const start = (initialSection && findSection(initialSection)) || firstSection();

		$scope.section = start.key;
		$scope.selectedPracticeId = start.practiceId;
		// The person or role to open the section on; only for the section the dialog opened on.
		$scope.focus = initialFocus;

		// Never open on a section whose group is folded shut - e.g. a tip deep-linking
		// into a practice the user is not currently working in.
		if (start.practiceId) {
			openOnly(start.practiceId);
		}

		$scope.selectSection = function (key, practiceId) {
			// Picking the same section for a different practice leaves ng-if true, so the
			// pane would keep the previous practice's data and save back to it. Drop the
			// pane for one digest to force a rebuild - which also replays the scope band.
			const sameSectionOtherPractice = $scope.section === key && $scope.selectedPracticeId !== practiceId;

			$scope.selectedPracticeId = practiceId;
			$scope.focus = null;

			if (!sameSectionOtherPractice) {
				$scope.section = key;
				return;
			}

			$scope.section = null;
			$timeout(function () {
				$scope.section = key;
			});
		};

		$scope.isActive = function (key, practiceId) {
			return $scope.section === key && $scope.selectedPracticeId === practiceId;
		};
	},
]);
